import { getConnectedEdges, getIncomers, getOutgoers } from "reactflow";
import _ from 'lodash';

// Converts a studio graph (save = { nodes, edges }) into a workflow.
// Pure: no store access, no generated ids. `newId` is only used when there is
// no existingWorkflow, so callers (and tests) control the id.
export function buildWorkflow(save, flowId, preview = false, existingWorkflow = undefined, newId = undefined) {

    // the start node is found by id, its position in nodes is not fixed
    const startNode = save.nodes.find(node => node.id === 'startOfWorkflow')
    if (!startNode) {
        throw new Error('buildWorkflow: no start node (startOfWorkflow) in flow ' + flowId)
    }

    let workflow

    if (existingWorkflow) {
        workflow = _.cloneDeep(existingWorkflow)
        workflow.title = startNode.data.title
        workflow.text = startNode.data.description
        workflow.media = mapMedia(startNode.data.media, preview)
        workflow.overlays = mapOverlays(startNode.data.overlays, preview)
        workflow.steps = []
    } else {
        workflow = {
            id: newId,
            title: startNode.data.title,
            text: startNode.data.description,
            media: mapMedia(startNode.data.media, preview),
            overlays: mapOverlays(startNode.data.overlays, preview),
            steps: [],
            owned: true,
            step: -1,
            studio: { id: flowId }
        }
    }

    let orderedNodes = dfsDefer(save, startNode)

    orderedNodes.forEach((node, index) => {

        const connectedEdges = getConnectedEdges([node], save.edges)

        if (node.data.nodeType !== 'decideNode') {
            const targetIndex = orderedNodes.findIndex(n => n.id == connectedEdges.find(edge => edge.source == node.id && edge.sourceHandle == 'output').target)

            if (targetIndex === index + 1) {
                workflow.steps.push(buildStep(node, undefined, preview))
            } else if (targetIndex === -1) {
                workflow.steps.push(buildStep(node, -2, preview))
            } else {
                workflow.steps.push(buildStep(node, targetIndex, preview))
            }
        } else {
            let yesIndex = -2
            let noIndex = -2

            connectedEdges.filter(edge => edge.source == node.id).forEach(edge => {
                if (edge.sourceHandle === 'yes') {
                    yesIndex = orderedNodes.findIndex(n => n.id == edge.target)
                    if (yesIndex === -1) {
                        yesIndex = -2
                    } else if (yesIndex === index + 1) {
                        yesIndex = -1
                    }
                }
                if (edge.sourceHandle === 'no') {
                    noIndex = orderedNodes.findIndex(n => n.id == edge.target)
                    if (noIndex === -1) {
                        noIndex = -2
                    } else if (noIndex === index + 1) {
                        noIndex = -1
                    }
                }
            })

            workflow.steps.push(buildStep(node, [yesIndex, noIndex], preview))
        }
    })

    if (preview) {
        // The simulator must always use the current editor state, even when
        // the studio workflow has never been exported or its remote id has
        // changed. The explicit flag keeps the preview local without using
        // the id length as an implicit workflow type.
        workflow.id = `simulator:${flowId}`
        workflow.workflowId = undefined
        workflow.simulatorPreview = true
        workflow.step = -1
        workflow.locked = false
        workflow.done = undefined
    } else {
        delete workflow.simulatorPreview
    }

    return workflow
}

function dfsDefer(graph, start) {
    const stack = [start];
    const visited = new Set();
    const result = [];

    while (stack.length) {
        const node = stack.pop();

        if (!visited.has(node)) {
            visited.add(node);
            if (node.data.nodeType != 'workflowStartNode' && node.data.nodeType != 'endNode') {
                result.push(node);
            }

            const incomers = getIncomers(node, graph.nodes, graph.edges)
            const outgoers = getOutgoers(node, graph.nodes, graph.edges)
            if (incomers.length > 1) {
                // in case of a join node defer following nodes
                for (const neighbor of outgoers) {
                    stack.unshift(neighbor);
                }
            } else {
                for (const neighbor of outgoers) {
                    stack.push(neighbor);
                }
            }
        }
    }

    return result;
}

function dfsJoin(graph, start) {
    const stack = [start];
    const visited = new Set();
    const result = [];
    const joins = {}

    while (stack.length) {
        const node = stack.pop();

        if (joins[node.id]) {
            joins[node.id]--
            if (joins[node.id] != 0) {
                continue
            }
            // console.log('NEXT', node.data.title)
        } else {
            const incomers = getIncomers(node, graph.nodes, graph.edges)
            if (!visited.has(node) && stack.length > 0 && incomers.length > 1) {
                joins[node.id] = incomers.length - 1
                result.push(node);
                // console.log('WAIT', node.data.title)
                continue
            }
        }

        if (!visited.has(node)) {
            visited.add(node);
            if (joins[node.id] == undefined && node.data.nodeType != 'workflowStartNode' && node.data.nodeType != 'endNode') {
                result.push(node);
            }

            for (const neighbor of getOutgoers(node, graph.nodes, graph.edges)) {
                stack.push(neighbor);
            }
        }
    }

    // console.log('JOINS', Object.keys(joins).map(id => ({j: joins[id], t:graph.nodes.find(n => n.id == id).data.title})), result.length, graph.nodes.length)

    return result;
}

function dfs(graph, start) {
    const stack = [start];
    const visited = new Set();
    const result = [];

    while (stack.length) {
        const node = stack.pop();

        if (!visited.has(node)) {
            visited.add(node);
            if (node.data.nodeType != 'workflowStartNode' && node.data.nodeType != 'endNode') {
                result.push(node);
            }

            for (const neighbor of getOutgoers(node, graph.nodes, graph.edges)) {
                stack.push(neighbor);
            }
        }
    }

    return result;
}

// A preview reads the existing Studio media directly. A regular export keeps
// using file:// so updateWorkflow can copy and replace the media ids as before.
const mediaReference = (id, preview) => (preview ? '' : 'file://') + id

const mapMedia = (media, preview) => (media || []).map(id => mediaReference(id, preview))

const mapOverlays = (overlays, preview, media) => Object.keys(overlays || {})
    .filter(key => !media || media.includes(key))
    .reduce((result, key) => {
        result[preview ? mediaReference(key, true) : key] = overlays[key]
        return result
    }, {})

function buildStep(node, goto, preview) {
    switch (node.data.nodeType) {
        case 'doNode':
            return {
                title: node.data.title,
                text: node.data.description,
                media: mapMedia(node.data.media, preview),
                overlays: mapOverlays(node.data.overlays, preview),
                type: 0,
                goto: goto                  // if goto === -2 jumps to end, goto = undefined is next step
            }
        case 'checklistNode':
            const overlays = mapOverlays(node.data.overlays, preview, node.data.media)
            return {
                title: node.data.title,
                text: node.data.description,
                media: mapMedia(node.data.media, preview),
                overlays: overlays,
                type: 1,
                check: node.data?.elements?.length > 0 ? node.data.elements.map((element, index) => {
                    const elementId = element.id ? element.id : node.id + 'element' + index
                    const elementMedia = node.data.listElementMedia?.[elementId] || []
                    const media = mapMedia(elementMedia, preview)
                    const overlays = mapOverlays(node.data.overlays, preview, elementMedia)
                    return {
                        title: element.title,
                        text: element.description,
                        media: media,
                        overlays: overlays,
                        done: false
                    }
                }) : [],
                goto: goto
            }
        case 'waitNode':
            return {
                title: node.data.title,
                text: node.data.description,
                media: mapMedia(node.data.media, preview),
                overlays: mapOverlays(node.data.overlays, preview),
                type: 2,
                timer: getTime(node.data.timer),
                goto: goto
            }
        case 'decideNode':
            return {
                title: node.data.title,
                text: node.data.description,
                media: mapMedia(node.data.media, preview),
                overlays: mapOverlays(node.data.overlays, preview),
                type: 3,
                yes: goto[0],        // -1 is next step, -2 jumps to end, nat. number is goto step
                no: goto[1],
            }
    }
}


function getTime(timeString) {
    if (typeof timeString === 'string' || timeString instanceof String) {
        const times = timeString.split(':')
        const seconds = parseInt(times[0]) * 3600 + parseInt(times[1]) * 60 + parseInt(times[2])
        if (!isNaN(seconds) && (typeof seconds === 'number' || seconds instanceof Number)) {
            return seconds
        }
    }
    return 0
}
